import { useState } from 'react';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { Box, Button, Link, styled } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import {
  ColourEnergyIndicator,
  TextV2,
} from '@insights-ltd/design-library/components';
import {
  dateInPastForZone,
  formatDate,
} from '@insights-ltd/design-library/utils';
import {
  EventResponseWithChapters,
  InviteeResponse,
  useEmailEventProfiles,
} from 'api';
import {
  isIdtlExperience,
  isExploreExperience,
  isDiscoveryExperience,
} from 'domain/event';
import EmailProgressDialog from 'components/EmailProgressDialog';
import LeadingColourEnergyIndicator from 'components/LeadingColourEnergyIndicator';
import DistributionMethodDialog from 'components/DistributionMethodDialog';
import { CHAPTERS } from 'variables';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { formatList } from 'utils/formatList';

type LearnersListRowProps = {
  invitee: InviteeResponse;
  deleteInvitee: (inviteeId: string) => void;
  resendInvite?: (inviteeId: string) => void;
  isLastRow: boolean;
  event: EventResponseWithChapters;
  readyToPurchase: boolean;
  readyToSend: boolean;
};

const StyledP = styled(TextV2)({
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const StyledNameEmail = styled(TextV2)({
  maxWidth: '260px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const LearnersListRow = ({
  deleteInvitee,
  resendInvite,
  invitee,
  isLastRow,
  event,
  readyToPurchase,
  readyToSend,
}: LearnersListRowProps) => {
  const { t, i18n } = useTranslation();
  const handleDeleteInvites = () => deleteInvitee(invitee.id);
  const [sendingProfile, setSendingProfile] = useState(false);
  const [showChoosingDistDialog, setShowChoosingDistDialog] = useState(false);
  const { mutate: emailProfiles } = useEmailEventProfiles();
  const isExplore = isExploreExperience(event.eventType);

  const chapters = formatList(
    invitee.chapters
      .map((chapter) => CHAPTERS.find((el) => el.id === chapter.name)?.titleKey)
      .filter((chapterKey) => chapterKey !== undefined)
      .map((chapterKey) => t(chapterKey)),
    i18n.language,
  );

  const handleSendEmail = (format?: string) => {
    setShowChoosingDistDialog(false);
    setSendingProfile(true);
    emailProfiles({
      eventId: event.id,
      inviteeIds: [invitee.id],
      dialect: invitee.dialect,
      generateUsing: 'EVALUATION_DIALECT',
      format,
    });
  };

  const shareProfile = () => {
    if (isExplore) {
      handleSendEmail('share-digital-profile-link');
    } else {
      setShowChoosingDistDialog(true);
    }
  };

  const resendEnabled =
    event?.eventStatus !== 'CANCELLED' &&
    !dateInPastForZone(event?.startsAt, event?.timezone);
  return (
    <>
      <Grid
        container
        spacing={1}
        data-testid="learners-list-row"
        sx={[
          {
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          },
          (th) => ({
            padding: `${th.spacing(spacingSizeMap.S)} ${th.spacing(
              spacingSizeMap.M,
            )}`,
            color: th.palette.grey[600],
            wordBreak: 'break-word',
          }),
        ]}
      >
        <Grid size={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {!isExplore && (
              <LeadingColourEnergyIndicator
                leadingColourEnergy={invitee.leadingColourEnergy}
              />
            )}
            <StyledNameEmail>{invitee.fullName}</StyledNameEmail>
          </Box>
          <StyledNameEmail>
            <Link
              target="_blank"
              href={`mailto:${invitee.email}`}
              rel="noopener noreferrer"
            >
              {invitee.email}
            </Link>
          </StyledNameEmail>
        </Grid>
        {!isIdtlExperience(event.eventType) && (
          <Grid size={4}>
            <TextV2>{chapters}</TextV2>
            {['REUSED', 'AVAILABLE_FOR_REUSE'].includes(
              invitee.profile?.status || '',
            ) && (
              <StyledP>
                <CheckCircleIcon htmlColor="#0d6c51" /> &nbsp;
                {t('ui.event-management.invite-list.reused-profile.label')}
              </StyledP>
            )}
            {invitee.scores && isExplore && (
              <Box data-testid="colour-scores" sx={{ maxWidth: '330px' }}>
                <ColourEnergyIndicator
                  blue={invitee.scores.blue}
                  green={invitee.scores.green}
                  yellow={invitee.scores.yellow}
                  red={invitee.scores.red}
                  preferred={invitee.scores.preferred}
                />
              </Box>
            )}
          </Grid>
        )}
        <Grid
          container
          size={3}
          sx={{
            flexDirection: 'column',
            justifyContent: 'flex-start',
            textAlign: 'right',
          }}
        >
          {resendEnabled && resendInvite && (
            <Grid>
              <Button onClick={() => resendInvite(invitee.id)}>
                <TextV2 component="span" variant="bodyBold" color="primary">
                  {t('ui.event-management.invite-list.resend-invite')}
                </TextV2>
              </Button>
            </Grid>
          )}
          {resendEnabled && resendInvite && invitee?.lastInviteAt && (
            <Grid>
              <TextV2>
                {t('ui.event-management.invite-list.last-sent', {
                  lastInviteAt: formatDate(invitee?.lastInviteAt || ''),
                })}
              </TextV2>
            </Grid>
          )}
          {readyToPurchase && invitee?.evaluation?.submittedOn && (
            <Grid>
              <TextV2>
                {t('ui.event-management.invite-list.completed-on', {
                  completedAt: formatDate(invitee.evaluation.submittedOn),
                })}
              </TextV2>
            </Grid>
          )}
          {invitee?.profileLastSentAt ? (
            <Grid>
              <Button onClick={shareProfile} sx={{ padding: 0 }}>
                <TextV2 variant="bodyBold" color="primary">
                  {t('ui.event-management.invite-list.share-profile')}
                </TextV2>
              </Button>
              <TextV2>
                {t('ui.event-management.invite-list.profile-last-shared', {
                  profileLastSentAt: formatDate(
                    invitee?.profileLastSentAt || '',
                  ),
                })}
              </TextV2>
            </Grid>
          ) : (
            readyToSend && (
              <Grid>
                <Button onClick={shareProfile} sx={{ padding: 0 }}>
                  <TextV2 variant="bodyBold" color="primary">
                    {t('ui.event-management.invite-list.share-profile')}
                  </TextV2>
                </Button>
              </Grid>
            )
          )}
        </Grid>
        <Grid
          size={1}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
          }}
        >
          <Button
            sx={{ padding: 0, minWidth: 0 }}
            onClick={handleDeleteInvites}
            aria-label={t(
              'ui.event-management.events.invitee.confirm-delete.title',
            )}
          >
            <TextV2 variant="bodyBold" color="error">
              {t('ui.event-management.invite-list.remove-user')}
            </TextV2>
          </Button>
        </Grid>
      </Grid>
      {!isLastRow && <Divider variant="fullWidth" />}
      <DistributionMethodDialog
        open={showChoosingDistDialog}
        profileCount={1}
        onSendViaLink={handleSendEmail}
        onSendViaEmail={() => handleSendEmail('attachment')}
        onCancel={() => setShowChoosingDistDialog(false)}
        showDigitalProfileLink={isDiscoveryExperience(event?.eventType)}
      />
      <EmailProgressDialog
        singleProfile
        open={sendingProfile}
        onClose={() => setSendingProfile(false)}
      />
    </>
  );
};

export default LearnersListRow;
